import React, { useState, useEffect } from 'react';
import api from '../api';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PrimaryButton from '../components/PrimaryButton';
import LeftPanel from '../components/LeftPanel';
import Step from '../components/Step';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { sendSupportTicket } from '../api/support';
import InfoModal from '../components/InfoModal';

function Signup() {
    const location = useLocation();
    const { state } = location;
    const schoolId = state?.schoolId;
    const schoolName = state?.schoolName;
    const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '' });
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [touched, setTouched] = useState({ firstName: false, lastName: false, email: false, password: false, confirmPassword: false });
    const [serverError, setServerError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const navigate = useNavigate();
    const [infoOpen, setInfoOpen] = useState(false);
    const [openSupport, setOpenSupport] = useState(false);
    const [supportFirstName, setSupportFirstName] = useState('');
    const [supportLastName, setSupportLastName] = useState('');
    const [supportSchoolName, setSupportSchoolName] = useState(schoolName || '');
    const [supportEmail, setSupportEmail] = useState('');
    const [supportType, setSupportType] = useState('');
    const [supportReason, setSupportReason] = useState('');
    const [supportLoading, setSupportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

    useEffect(() => {
        if (!schoolId) {
            navigate('/eligibilitycheck');
        }
    }, [schoolId, navigate]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleBlur = (field) => {
        setTouched((t) => ({ ...t, [field]: true }));
    };

    const toggleShowPassword = () => setShowPassword((s) => !s);
    const toggleShowConfirmPassword = () => setShowConfirmPassword((s) => !s);

    const validation = {
        firstName: form.firstName.trim().length > 0,
        lastName: form.lastName.trim().length > 0,
        email: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email),
        password: form.password.length >= 8,
        confirmPassword: form.confirmPassword === form.password,
    };

    const formValid = Object.values(validation).every(Boolean);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setTouched({ firstName: true, lastName: true, email: true, password: true, confirmPassword: true });
        const validate = () => ({
            firstName: form.firstName.trim().length > 0,
            lastName: form.lastName.trim().length > 0,
            email: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email),
            password: form.password.length >= 8,
            confirmPassword: form.confirmPassword === form.password,
        });

        const validMap = validate();
        const formValid = Object.values(validMap).every(Boolean);
        if (!formValid) return;
        try {
            setSubmitting(true);
            setServerError('');
            setFieldErrors({});
            // Pass schoolId to backend
            const res = await api.post('/api/auth/signup', { ...form, schoolId }, { params: { password: form.password } });
            if (res && res.data) {
                if (res.data.fieldErrors) {
                    setFieldErrors(res.data.fieldErrors);
                    return;
                }
                // On success, set onboarding flag so new admins see InviteCollab after first login,
                // then navigate to OTP and pass email
                try { localStorage.setItem('showInviteOnFirstLogin', 'true'); } catch (e) { }
                // Notify external email system (best-effort, do not block user flow)
                try {
                    api.post('/api/internal/proxy/signup-notify', { email: form.email, firstName: form.firstName, lastName: form.lastName, schoolId: schoolId, schoolName: schoolName })
                        .catch(() => { /* best-effort */ });
                } catch (e) { /* ignore */ }
                try { localStorage.setItem('pendingEmail', form.email); } catch (e) { }
                navigate('/otp', { state: { email: form.email } });
            }
        } catch (err) {
            console.error(err);
            const data = err?.response?.data;
            if (data) {
                // Check for unique email violation (Postgres or backend error)
                if (data.message && data.message.toLowerCase().includes('unique') && data.message.toLowerCase().includes('email')) {
                    setFieldErrors({ email: "We're sorry but this email address has already been assigned to a school, please use a unique email address." });
                    setServerError('');
                } else if (data.fieldErrors) {
                    setFieldErrors(data.fieldErrors);
                } else if (data.errors) {
                    if (Array.isArray(data.errors)) {
                        const map = {};
                        data.errors.forEach((e) => {
                            if (e.field) map[e.field] = e.message || e.defaultMessage || e.msg;
                        });
                        setFieldErrors(map);
                    } else if (typeof data.errors === 'object') {
                        setFieldErrors(data.errors);
                    }
                } else if (data.message) {
                    setServerError(data.message);
                } else {
                    setServerError('Failed to create account');
                }
            } else {
                setServerError('Failed to create account');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenSupport = () => {
        setSupportFirstName(form.firstName || '');
        setSupportLastName(form.lastName || '');
        setSupportSchoolName(schoolName || '');
        setSupportEmail(form.email || '');
        setSupportType('');
        setSupportReason('');
        setSnackbar({ open: false, severity: 'success', message: '' });
        setOpenSupport(true);
    };

    const handleCloseSupport = () => {
        setOpenSupport(false);
    };

    const handleSubmitSupport = async () => {
        setSupportLoading(true);
        try {
            await sendSupportTicket({ firstName: supportFirstName, lastName: supportLastName, schoolName: supportSchoolName, email: supportEmail, supportType, additionalInformation: supportReason });
            setSnackbar({ open: true, severity: 'success', message: 'Support request sent!' });
            setTimeout(() => {
                setSnackbar({ open: false, severity: 'success', message: '' });
                setOpenSupport(false);
            }, 1500);
        } catch (err) {
            setSnackbar({ open: true, severity: 'error', message: err?.response?.data?.message || 'Failed to send support request' });
        } finally {
            setSupportLoading(false);
        }
    };

    return (
        <Box component="main">
            <Grid container spacing={4} alignItems="flex-start" sx={{ mt: 2 }}>
                <Grid item xs={12} md={4} lg={3}>
                    <LeftPanel items={[
                        <Step key="step1">1. Create your account</Step>,
                        'You’ll become the application admin.',

                        <Step key="step2">2. Check your email</Step>,
                        'We’ll send you a one-time verification code.',

                        <Step key="step3">3. Verify & continue</Step>,
                        'Enter the code to move forward.',

                        <Step key="step4">4. Invite your team</Step>,
                        'Add collaborators.'
                    ]}>
                        <PrimaryButton key="faq-btn" onClick={() => { setInfoOpen(true); }} sx={{ bgcolor: '#fff', color: '#000', textTransform: 'none', flex: '0 0 calc(50% - 6px)' }}>FAQ</PrimaryButton>
                        <PrimaryButton key="support-btn" onClick={() => { setSupportFirstName(form.firstName || ''); setSupportLastName(form.lastName || ''); setSupportSchoolName(schoolName || ''); setSupportEmail(form.email || ''); setSupportType(''); setSupportReason(''); setSnackbar({ open: false, severity: 'success', message: '' }); setOpenSupport(true); }} sx={{ bgcolor: '#fff', color: '#000', textTransform: 'none', flex: '0 0 calc(50% - 6px)' }}>Support</PrimaryButton>
                    </LeftPanel>
                </Grid>

                <Grid item xs={12} md={8} lg={6}>
                    <Paper elevation={1} sx={{ p: { xs: 3, md: 4 } }}>

                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>
                            Congratulations{' '}
                            <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>{schoolName}{!schoolName ? '' : '!'}</Box>
                            <br />
                            <Box component="span">Your school is eligible for the Retool Your School Program. Let's start by creating your account.</Box>
                        </Typography>
                        {/* School name is shown in the Congratulations heading above; remove duplicate subtitle. */}

                        {serverError ? <Alert severity="error" sx={{ mb: 2 }}>{serverError}</Alert> : null}

                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="firstName"
                                        label="First Name"
                                        placeholder="Enter your name"
                                        value={form.firstName}
                                        onChange={handleChange}
                                        onBlur={() => handleBlur('firstName')}
                                        error={(touched.firstName && !form.firstName) || Boolean(fieldErrors.firstName)}
                                        helperText={fieldErrors.firstName || (touched.firstName && !form.firstName ? 'First name is required' : '')}
                                        fullWidth
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="lastName"
                                        label="Last Name"
                                        placeholder="Enter your name"
                                        value={form.lastName}
                                        onChange={handleChange}
                                        onBlur={() => handleBlur('lastName')}
                                        error={(touched.lastName && !form.lastName) || Boolean(fieldErrors.lastName)}
                                        helperText={fieldErrors.lastName || (touched.lastName && !form.lastName ? 'Last name is required' : '')}
                                        fullWidth
                                        required
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        name="email"
                                        type="email"
                                        label="Email"
                                        placeholder="Enter your email"
                                        value={form.email}
                                        onChange={handleChange}
                                        onBlur={() => handleBlur('email')}
                                        error={Boolean(fieldErrors.email) || (touched.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email))}
                                        helperText={fieldErrors.email || (touched.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email) ? 'Enter a valid email' : '')}
                                        fullWidth
                                        required
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        label="Password"
                                        placeholder="Enter your password"
                                        value={form.password}
                                        onChange={handleChange}
                                        onBlur={() => handleBlur('password')}
                                        error={Boolean(fieldErrors.password) || (touched.password && form.password.length < 8)}
                                        helperText={fieldErrors.password || (touched.password && form.password.length < 8 ? 'Password must be at least 8 characters' : '')}
                                        fullWidth
                                        required
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={toggleShowPassword} edge="end" size="large" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        name="confirmPassword"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        label="Confirm Password"
                                        placeholder="Confirm your password"
                                        value={form.confirmPassword}
                                        onChange={handleChange}
                                        onBlur={() => handleBlur('confirmPassword')}
                                        error={Boolean(fieldErrors.confirmPassword) || (touched.confirmPassword && form.confirmPassword !== form.password)}
                                        helperText={fieldErrors.confirmPassword || (touched.confirmPassword && form.confirmPassword !== form.password ? 'Passwords do not match' : '')}
                                        fullWidth
                                        required
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={toggleShowConfirmPassword} edge="end" size="large" aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>
                                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <PrimaryButton type="submit" disabled={!formValid || submitting} sx={{ px: 6 }}>
                                            {submitting ? 'Creating...' : 'Create Account'}
                                        </PrimaryButton>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
            <Dialog open={openSupport} onClose={handleCloseSupport} fullWidth maxWidth="sm">
                <DialogTitle>Support Request</DialogTitle>
                <DialogContent>
                    {snackbar.open && (
                        <Alert severity={snackbar.severity} sx={{ mb: 2 }}>
                            {snackbar.message}
                        </Alert>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="First name" value={supportFirstName} onChange={(e) => setSupportFirstName(e.target.value)} fullWidth required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                        <TextField label="Last name" value={supportLastName} onChange={(e) => setSupportLastName(e.target.value)} fullWidth required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                        <TextField label="School name" value={supportSchoolName} onChange={(e) => setSupportSchoolName(e.target.value)} fullWidth required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                        <TextField label="Email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} fullWidth required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                        <FormControl fullWidth required>
                            <InputLabel id="support-type-label" sx={{ '& .MuiFormLabel-asterisk': { color: '#f57c00' } }}>Choose your type of support</InputLabel>
                            <Select labelId="support-type-label" value={supportType} label={"Choose your type of support"} onChange={(e) => setSupportType(e.target.value)}>
                                <MenuItem value={"My school is not eligible"}>My school is not eligible</MenuItem>
                                <MenuItem value={"I'm having trouble with my application"}>I'm having trouble with my application</MenuItem>
                                <MenuItem value={"I'm having trouble inviting a collaborator"}>I'm having trouble inviting a collaborator</MenuItem>
                                <MenuItem value={"I didn't receive my email verification"}>I didn't receive my email verification</MenuItem>
                                <MenuItem value={"Other"}>Other</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField label="Additional information" value={supportReason} onChange={e => setSupportReason(e.target.value)} fullWidth multiline minRows={3} required InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#f57c00' } } }} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseSupport} disabled={supportLoading}>Cancel</Button>
                    <Button onClick={handleSubmitSupport} variant="contained" disabled={supportLoading || !supportReason.trim() || !supportFirstName.trim() || !supportLastName.trim() || !supportSchoolName.trim() || !supportEmail.trim() || !supportType}>
                        {supportLoading ? 'Sending...' : 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>

            <InfoModal open={infoOpen} onClose={() => setInfoOpen(false)} />
        </Box>
    );
}

export default Signup;
